import {createViewConfig, media} from '@sail/engine';
import {css, tokens} from '@sail/ui';
import {helmTokens} from '@sail/helm';
import {useId} from '@sail/react-aria';
import {useMemo} from 'react';
import {STRIPE_ENV, useStripeEnv} from 'mkt-ssr/lib/env';

import type {CustomerLogoWrapperProps} from 'mkt-ssr/components/sail/CustomerLogo/CustomerLogoWrapper';
import {
  ALL_CUSTOMER_LOGOS,
  CustomerLogoName,
  isValidCustomerLogo,
} from 'mkt-ssr/components/sail/CustomerLogo/CustomerLogoImports';

export type CustomerLogoColorWay = 'fullColor' | 'flat';

type CustomerLogoSvgProps = CustomerLogoWrapperProps & {
  /**
   * The variant of the logo to be displayed
   */
  colorWay?: CustomerLogoColorWay;
};

export type CustomerLogoRenderProps = CustomerLogoSvgProps & {
  contextId: string;
};

export type CustomerLogoProps = CustomerLogoSvgProps & {
  /**
   * The customer logo to be displayed
   */
  logo:
    | CustomerLogoName
    | React.ComponentType<React.PropsWithChildren<CustomerLogoRenderProps>>;
};

export const CustomerLogoConfig = createViewConfig({
  props: {} as CustomerLogoProps,
  name: 'CustomerLogo',
  defaults: {
    colorWay: 'fullColor',
  },
});

/**
 * Renders a Stripe customer logo as an SVG
 */
export const CustomerLogo = CustomerLogoConfig.createView(
  ({logo, ...props}) => {
    const id = useId();
    const environment = useStripeEnv();

    const LogoComponent = useMemo(() => {
      if (typeof logo === 'string' && isValidCustomerLogo(logo)) {
        return ALL_CUSTOMER_LOGOS[logo];
      } else if (typeof logo === 'function') {
        return logo;
      }
      return environment !== STRIPE_ENV.PROD
        ? () => <div style={{color: 'red'}}>Logo not found: {logo}</div>
        : null;
    }, [logo, environment]);

    if (!LogoComponent) return null;

    return <LogoComponent contextId={id} {...props} />;
  },
  {
    uses: [
      css({
        transition: `${helmTokens.transition.hover}`,
        maxWidth: 'none',
        [media.down(tokens.viewport.large)]: {
          maxWidth: '160px',
        },
      }),
    ],
    variants: {
      colorWay: {
        flat: {
          uses: [
            css({
              '--userLogoColor': `${helmTokens.color.customerLogoColor}`,
              '--userLogoColorAlt': `${tokens.color.background.surface}`,
            }),
          ],
        },
      },
    },
    forward: {
      colorWay: true,
    },
  },
);
